import { pick } from 'es-toolkit'
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P]
}

interface IReservationState {
  car: {
    brand: string | null
    brandId: number | null
    model: string | null
    modelId: number | null
    detailModel: string | null
    detailModelId: number | null
    fuelId: string | null
  }
  reservationInfo: {
    reservationId: number | null
    date: string | null
    time: string | null
    placeId: number | null
    placeName: string | null
    /** 예약 플로우 유형 (boxTruck이면 결제 시 탑차 전용 쿠폰 발급) */
    type: string | null
  }
  product: {
    id: number | null
    groupId: number | null
    originalPrice: number
    discountedPrice: number
    discountRate: number
  }
  payment: {
    finalAmount: number
  }
}

interface IReservationActions {
  setReservationState: (params: {
    car?: DeepPartial<IReservationState['car']>
    product?: DeepPartial<IReservationState['product']>
    reservationInfo?: DeepPartial<IReservationState['reservationInfo']>
    payment?: DeepPartial<IReservationState['payment']>
  }) => void
  resetReservationState: (keys?: Array<keyof IReservationState>) => void
}

interface IReservationStore {
  state: IReservationState
  actions: IReservationActions
}

interface IPersistedState {
  state: IReservationState
}

const initialState: IReservationState = {
  car: {
    brand: null,
    brandId: null,
    model: null,
    modelId: null,
    detailModel: null,
    detailModelId: null,
    fuelId: null,
  },
  reservationInfo: {
    reservationId: null,
    date: null,
    time: null,
    placeId: null,
    placeName: null,
    type: null,
  },
  product: {
    id: null,
    groupId: null,
    originalPrice: 0,
    discountedPrice: 0,
    discountRate: 0,
  },
  payment: {
    finalAmount: 0,
  },
}

/**
 * 예약 정보를 관리하는 Zustand Store
 *
 * @example
 * ```tsx
 * const { state, actions } = useReservationStore()
 * const { setReservationState, resetReservationState } = actions
 *
 * // 전체 정보 설정
 * setReservationState({
 *   product: {
 *     id: 1,
 *     groupId: 1,
 *   },
 * })
 *
 * // 일부 정보만 업데이트 (나머지는 유지됨 - Deep Merge)
 * setReservationState({
 *   product: {
 *     id: 1,
 *   },
 * })
 *
 * // 리셋
 * resetState() // 전체 리셋
 * resetState(['product']) // product만 리셋
 * ```
 */
const useReservationStore = create<IReservationStore>()(
  persist(
    set => ({
      state: initialState,
      actions: {
        setReservationState: ({ car, product, reservationInfo, payment }) =>
          set(store => ({
            state: {
              ...store.state,
              ...(car && {
                car: {
                  ...store.state.car,
                  ...car,
                },
              }),
              ...(product && {
                product: {
                  ...store.state.product,
                  ...product,
                },
              }),
              ...(reservationInfo && {
                reservationInfo: {
                  ...store.state.reservationInfo,
                  ...reservationInfo,
                },
              }),
              ...(payment && {
                payment: {
                  ...store.state.payment,
                  ...payment,
                },
              }),
            },
          })),
        resetReservationState: keys =>
          set(store => ({
            state: keys
              ? { ...store.state, ...pick(initialState, keys) }
              : initialState,
          })),
      },
    }),
    {
      name: 'reservation-store',
      storage: createJSONStorage(() => sessionStorage),
      partialize: state => ({ state: state.state }),
      merge: (persistedState, currentState) => {
        const typedPersistedState = persistedState as
          | IPersistedState
          | undefined

        if (!typedPersistedState?.state) return currentState

        return {
          ...currentState,
          state: {
            car: {
              ...currentState.state.car,
              ...typedPersistedState.state.car,
            },
            product: {
              ...currentState.state.product,
              ...typedPersistedState.state.product,
            },
            reservationInfo: {
              ...currentState.state.reservationInfo,
              ...typedPersistedState.state.reservationInfo,
            },
            payment: {
              ...currentState.state.payment,
              ...typedPersistedState.state.payment,
            },
          },
        }
      },
    }
  )
)

export default useReservationStore
